package pl.mothdigital.neuronowi.pages.specialists

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.*
import com.varabyte.kobweb.compose.ui.*
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.core.*
import com.varabyte.kobweb.silk.components.graphics.*
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.layout.*
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.css.keywords.*
import org.jetbrains.compose.web.dom.*
import pl.mothdigital.neuronowi.common.resources.*
import pl.mothdigital.neuronowi.components.*
import pl.mothdigital.neuronowi.components.layouts.*
import pl.mothdigital.neuronowi.components.sections.cta.*
import pl.mothdigital.neuronowi.components.text.*
import pl.mothdigital.neuronowi.components.widgets.*
import pl.mothdigital.neuronowi.common.utils.fixPolishOrphans
import pl.mothdigital.neuronowi.theme.*
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.breakpoint.displayUntil
import org.jetbrains.compose.web.css.FlexWrap
import com.varabyte.kobweb.compose.css.AlignItems
import pl.mothdigital.neuronowi.common.model.SpecialistData

private val priceItems = listOf(
    PriceItem(
        StringResources.PAULINA_KONIECZNA_PRICE_1_NAME,
        StringResources.PAULINA_KONIECZNA_PRICE_1_TIME,
        StringResources.PAULINA_KONIECZNA_PRICE_1_AMOUNT
    ),
    PriceItem(
        StringResources.PAULINA_KONIECZNA_PRICE_2_NAME,
        StringResources.PAULINA_KONIECZNA_PRICE_2_TIME,
        StringResources.PAULINA_KONIECZNA_PRICE_2_AMOUNT
    ),
    PriceItem(
        StringResources.PAULINA_KONIECZNA_PRICE_3_NAME,
        StringResources.PAULINA_KONIECZNA_PRICE_3_TIME,
        StringResources.PAULINA_KONIECZNA_PRICE_3_AMOUNT
    )
)

@Page
@Composable
fun PaulinaKoniecznaPage() {
    SpecialistPageLayout(
        title = StringResources.PAULINA_KONIECZNA,
        headerSection = { HeaderSection() },
        profileSection = { ProfileSection() },
        specializationsSection = { SpecializationsSection() },
        servicesSection = { ServicesSection() },
        priceSection = { PriceSection() },
        ctaSection = { context ->
            CTASection(
                ctx = context,
                customAction = { js("window.scrollTo({ top: 0, behavior: 'smooth' })") },
            )
        }
    )
}

@Composable
private fun HeaderSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50)
            .toAttrs()
    ) {
        Column(
            modifier = Modifier.fillMaxWidth().margin { auto },
            verticalArrangement = Arrangement.spacedBy(1.cssRem)
        ) {
            AppH1 { Text(StringResources.PAULINA_KONIECZNA.fixPolishOrphans()) }
            AppP(color = SiteTheme.green._800, modifier = Modifier.fontSize(1.25.cssRem)) {
                Text(StringResources.PAULINA_KONIECZNA_ACADEMIC_TITLES.fixPolishOrphans())
            }
            AppP(
                color = SiteTheme.green._800,
                modifier = Modifier.fontSize(1.25.cssRem).fontWeight(FontWeight.Bold)
            ) {
                Text(StringResources.PAULINA_KONIECZNA_PHONE_NUMBER)
            }
        }
    }
}

@Composable
private fun ProfileSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        // Large screens: side-by-side image and text
        Row(
            modifier = Modifier
                .fillMaxWidth()
                .flexWrap(FlexWrap.Wrap)
                .displayIfAtLeast(Breakpoint.LG)
                .gap(3.cssRem),
        ) {
            Image(
                src = ImageResources.avatarPaulinaKonieczna.src,
                alt = ImageResources.avatarPaulinaKonieczna.description,
                modifier = Modifier
                    .width(400.px)
                    .borderRadius(1.cssRem)
                    .boxShadow(blurRadius = 8.px, color = SiteTheme.black._100)
            )
            Column(
                modifier = Modifier
                    .flex(1)
                    .gap(1.5.cssRem)
                    .padding(1.cssRem),
            ) {
                AboutMeContent()
            }
        }
        // Medium and smaller: image above text
        Column(
            modifier = Modifier
                .fillMaxWidth()
                .alignItems(AlignItems.Center)
                .displayUntil(Breakpoint.LG)
                .gap(1.5.cssRem)
                .padding(1.cssRem),
        ) {
            AboutMeContent()

            Image(
                src = ImageResources.avatarPaulinaKonieczna.src,
                alt = ImageResources.avatarPaulinaKonieczna.description,
                modifier = Modifier
                    .widthIn(min = 300.px, max = 500.px)
                    .fillMaxWidth()
                    .borderRadius(1.cssRem)
                    .boxShadow(blurRadius = 8.px, color = SiteTheme.black._100)
            )
        }
    }
}

@Composable
private fun AboutMeContent() {
    AppH2 { Text("O mnie") }
    listOf(
        StringResources.PAULINA_KONIECZNA_ABOUT_ME_1,
        StringResources.PAULINA_KONIECZNA_ABOUT_ME_2,
        StringResources.PAULINA_KONIECZNA_ABOUT_ME_3,
        StringResources.PAULINA_KONIECZNA_ABOUT_ME_4,
    ).forEach { paragraph ->
        AppP(modifier = Modifier.textAlign(TextAlign.Justify)) {
            Text(paragraph.fixPolishOrphans())
        }
    }
}

@Composable
private fun SpecializationsSection() {
    val specializations = listOf(
        SpecialistData(
            icon = { FaCommentMedical() },
            title = StringResources.PAULINA_KONIECZNA_SPEC_NEUROLOGOPEDIA_TITLE,
            description = StringResources.PAULINA_KONIECZNA_SPEC_NEUROLOGOPEDIA_DESC
        ),
        SpecialistData(
            icon = { FaEarDeaf() },
            title = StringResources.PAULINA_KONIECZNA_SPEC_APD_TITLE,
            description = StringResources.PAULINA_KONIECZNA_SPEC_APD_DESC
        )
    )

    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.PAULINA_KONIECZNA_SPECIALIZATIONS_TITLE.fixPolishOrphans()) }

            SimpleGrid(
                modifier = Modifier.fillMaxWidth().gap(1.5.cssRem),
                numColumns = numColumns(base = 1, sm = 1, md = 2, lg = 2, xl = 2)
            ) {
                specializations.forEach { info ->
                    SpecialistInfoCard(
                        icon = info.icon,
                        title = info.title,
                        description = info.description
                    )
                }
            }
        }
    }
}

@Composable
private fun ServicesSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.PAULINA_KONIECZNA_SERVICES_TITLE.fixPolishOrphans()) }

            Column(modifier = Modifier.gap(0.5.cssRem)) {
                Ul {
                    listOf(
                        StringResources.PAULINA_KONIECZNA_SERVICE_1,
                        StringResources.PAULINA_KONIECZNA_SERVICE_2,
                        StringResources.PAULINA_KONIECZNA_SERVICE_3,
                        StringResources.PAULINA_KONIECZNA_SERVICE_4,
                        StringResources.PAULINA_KONIECZNA_SERVICE_5,
                        StringResources.PAULINA_KONIECZNA_SERVICE_6,
                    ).forEach { Li { Text(it.fixPolishOrphans()) } }
                }
            }
        }
    }
}

@Composable
private fun PriceSection() {
    Section(
        attrs = Modifier
            .fillMaxWidth()
            .padding(topBottom = 3.cssRem, leftRight = 6.percent)
            .backgroundColor(SiteTheme.green._50.toRgb().copy(alpha = 128))
            .toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth().margin { auto }.gap(2.cssRem)) {
            AppH2 { Text(StringResources.PAULINA_KONIECZNA_PRICE_TITLE.fixPolishOrphans()) }

            SpecialistPriceTable(priceItems = priceItems)

            AppP(modifier = Modifier.fontSize(0.9.cssRem), color = SiteTheme.black._700) {
                Text(StringResources.PAULINA_KONIECZNA_PRICE_NOTE.fixPolishOrphans())
            }
        }
    }
}
